import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import { X, Check, Sparkles, Receipt, BookOpenCheck, LineChart, MessageSquareText, ChevronDown, ChevronUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const DONE_KEY = "powereffect.installed_tour.done.v1";
const MIN_KEY  = "powereffect.installed_tour.minimized.v1";
const STEP_KEY = "powereffect.installed_tour.step.v1";

/** True only when the app runs as an installed PWA (or an installed webapp
 *  on iOS Safari, which uses navigator.standalone instead of the CSS
 *  display-mode media query). Also honours a `?force_tour=1` URL param so
 *  QA (and Playwright headless) can preview the coachmark without needing
 *  a real install. The forced flag is persisted to sessionStorage so it
 *  survives SPA route guards that strip query strings during auth redirects. */
function isStandalone() {
  if (typeof window === "undefined") return false;
  const dm = window.matchMedia?.("(display-mode: standalone)")?.matches;
  const iosStandalone = window.navigator.standalone === true;
  let forced = false;
  try {
    const q = new URLSearchParams(window.location.search).get("force_tour") === "1";
    if (q) sessionStorage.setItem("powereffect.installed_tour.force", "1");
    forced = q || sessionStorage.getItem("powereffect.installed_tour.force") === "1";
  } catch { /* storage disabled — proceed */ }
  return !!(dm || iosStandalone || forced);
}

/** 4-step guided walkthrough that fires ONCE for the first launch of the
 *  installed app. Non-blocking bottom-right coachmark; user can minimize
 *  to a chip, skip entirely, or complete each step via "Take me there".
 *
 *  Mounted globally from <AppShell/>. Renders nothing when:
 *    • The browser is a normal tab (not installed), OR
 *    • The user has already completed / skipped the tour.
 */
export function InstalledAppTour() {
  const navigate = useNavigate();
  const { user } = useAuth();
  // Wait until the pre-existing first-launch OnboardingWizard is finished
  // before showing our coachmark — don't compete for the user's attention.
  const wizardActive = user && user.onboarding_completed === false && !user.is_demo;
  // Persist step across route changes: "Take me there" navigates the SPA,
  // which unmounts the previous route + remounts this component — we don't
  // want the tour to reset to step 1 every time.
  const [step, setStep] = useState(() => {
    if (typeof window === "undefined") return 0;
    try {
      const raw = sessionStorage.getItem(STEP_KEY);
      const n = raw == null ? 0 : parseInt(raw, 10);
      return Number.isFinite(n) && n >= 0 && n < 4 ? n : 0;
    } catch { return 0; }
  });
  const [minimized, setMinimized] = useState(false);
  // Synchronously decide on first render whether to show the tour — avoids
  // a race where the visible-check runs *after* Playwright/E2E already
  // queried the DOM, and also avoids a 800ms flash of the app before the
  // coachmark appears on a real install.
  const [visible, setVisible] = useState(() => {
    if (typeof window === "undefined") return false;
    if (!isStandalone()) return false;
    try {
      if (localStorage.getItem(DONE_KEY) === "1") return false;
    } catch { /* storage disabled — proceed */ }
    return true;
  });

  useEffect(() => {
    if (!visible) return;
    try { setMinimized(localStorage.getItem(MIN_KEY) === "1"); }
    catch (err) { console.debug("[InstalledAppTour] localStorage read failed:", err?.message); }
  }, [visible]);

  const STEPS = useMemo(() => ([
    {
      key: "invoice",
      Icon: Receipt,
      title: "Send your first invoice",
      body: "Create an invoice for a real customer — it takes about 30 seconds. This is the fastest way to see the whole ledger come to life.",
      cta: "Take me there",
      href: "/receivables?tab=invoices&new=1",
    },
    {
      key: "post",
      Icon: BookOpenCheck,
      title: "Post it to the ledger",
      body: "On any posted invoice, hit the ledger icon to auto-generate the journal entry. Your P&L and Balance Sheet update instantly.",
      cta: "Open Receivables",
      href: "/receivables?tab=invoices",
    },
    {
      key: "pnl",
      Icon: LineChart,
      title: "See your P&L",
      body: "Financial statements you can trust — click any line to drill down to the exact transactions that make up the total.",
      cta: "Open Reports",
      href: "/reports",
    },
    {
      key: "ai",
      Icon: MessageSquareText,
      title: "Ask the AI about your books",
      body: "Chat with a CFO who has full context on your numbers. Try: “How much runway do I have if I keep spending like last quarter?”",
      cta: "Try AI chat",
      href: "/ai",
    },
  ]), []);

  const total = STEPS.length;

  const dismiss = useCallback((reason = "skip") => {
    try {
      localStorage.setItem(DONE_KEY, "1");
      localStorage.setItem(`${DONE_KEY}.reason`, reason);
      localStorage.setItem(`${DONE_KEY}.at`, new Date().toISOString());
      sessionStorage.removeItem(STEP_KEY);
      sessionStorage.removeItem("powereffect.installed_tour.force");
    } catch (err) {
      console.debug("[InstalledAppTour] persist done failed:", err?.message);
    }
    setVisible(false);
  }, []);

  const advanceTo = useCallback((next) => {
    setStep(next);
    try { sessionStorage.setItem(STEP_KEY, String(next)); }
    catch (err) { console.debug("[InstalledAppTour] persist step failed:", err?.message); }
  }, []);

  const minimize = useCallback((v) => {
    setMinimized(v);
    try { localStorage.setItem(MIN_KEY, v ? "1" : "0"); }
    catch (err) { console.debug("[InstalledAppTour] persist min failed:", err?.message); }
  }, []);

  const takeMeThere = useCallback(() => {
    const s = STEPS[step];
    if (step === total - 1) {
      dismiss("completed");
      navigate(s.href);
    } else {
      advanceTo(step + 1);
      navigate(s.href);
    }
  }, [STEPS, step, total, navigate, dismiss, advanceTo]);

  if (!visible || wizardActive) return null;

  const current = STEPS[step];
  const CurrentIcon = current.Icon;

  // Minimized: small floating chip in the bottom-right corner.
  if (minimized) {
    return createPortal(
      <button
        type="button"
        data-testid="installed-tour-chip"
        onClick={() => minimize(false)}
        className="fixed bottom-4 right-4 z-[80] inline-flex items-center gap-2 px-3.5 py-2 rounded-full shadow-lg border border-[var(--brand)]/20 bg-[var(--brand)] text-white text-xs font-medium hover:brightness-110 transition"
      >
        <Sparkles className="w-3.5 h-3.5" />
        Tour · {step + 1}/{total}
        <ChevronUp className="w-3.5 h-3.5" />
      </button>,
      document.body,
    );
  }

  return createPortal(
    <div
      data-testid="installed-tour-card"
      className="fixed bottom-4 right-4 z-[80] w-[360px] max-w-[calc(100vw-2rem)] bg-white dark:bg-[var(--surface)] border border-[var(--border)] rounded-2xl shadow-2xl overflow-hidden"
      role="dialog"
      aria-live="polite"
      aria-label="Installed app tour"
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-3 border-b border-[var(--divider)] flex items-start gap-3">
        <div className="w-9 h-9 rounded-lg bg-[var(--brand-light)] text-[var(--brand-text)] flex items-center justify-center shrink-0">
          <CurrentIcon className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted-text)] flex items-center gap-1.5">
            <Sparkles className="w-3 h-3" /> Get started · {step + 1} of {total}
          </div>
          <h3 className="font-display text-lg mt-0.5 leading-tight" data-testid="installed-tour-title">
            {current.title}
          </h3>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <button
            type="button"
            data-testid="installed-tour-minimize"
            onClick={() => minimize(true)}
            aria-label="Minimize tour"
            className="w-7 h-7 inline-flex items-center justify-center rounded-md text-[var(--muted-text)] hover:bg-[var(--hover)]"
          >
            <ChevronDown className="w-4 h-4" />
          </button>
          <button
            type="button"
            data-testid="installed-tour-skip"
            onClick={() => dismiss("skip")}
            aria-label="Skip tour"
            className="w-7 h-7 inline-flex items-center justify-center rounded-md text-[var(--muted-text)] hover:bg-[var(--hover)]"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3 text-sm text-[var(--text)] leading-relaxed">
        {current.body}
      </div>

      {/* Progress dots */}
      <div className="px-4 pb-2 flex items-center gap-1.5" aria-hidden="true">
        {STEPS.map((s, i) => (
          <span
            key={s.key}
            className={`h-1 rounded-full transition-all ${
              i < step ? "bg-[var(--brand)] w-4" :
              i === step ? "bg-[var(--brand)] w-8" :
              "bg-[var(--border)] w-4"
            }`}
          />
        ))}
      </div>

      {/* Footer actions */}
      <div className="px-4 pb-4 pt-1 flex items-center justify-between gap-2">
        <button
          type="button"
          data-testid="installed-tour-later"
          onClick={() => dismiss("skip")}
          className="text-xs text-[var(--muted-text)] hover:text-[var(--text)] transition"
        >
          Skip tour
        </button>
        <div className="flex items-center gap-2">
          {step < total - 1 && (
            <Button
              variant="ghost"
              size="sm"
              data-testid="installed-tour-next"
              onClick={() => advanceTo(Math.min(total - 1, step + 1))}
              className="h-8 px-3 text-xs"
            >
              Next
            </Button>
          )}
          <Button
            size="sm"
            data-testid="installed-tour-cta"
            onClick={takeMeThere}
            className="h-8 px-3 text-xs bg-[var(--brand)] hover:bg-[var(--brand-hover)] text-white gap-1.5"
          >
            {step === total - 1 ? <><Check className="w-3.5 h-3.5" /> Finish</> : current.cta}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
